'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/Button';
import { Text } from '@/components/ui/Text';
import {
  type ConsentCategories,
  defaultConsent,
  getSavedConsent,
  saveConsent,
  applyConsentMode,
  shouldShowConsentBanner,
  isDNTEnabled,
  revokeConsent,
} from '@/lib/consent';
import { initializeAnalytics } from '@/lib/analytics';
import { ROUTES } from '@/lib/routes';
import styles from './ConsentBanner.module.css';

export interface ConsentBannerProps {
  locale?: string;
}

/**
 * GDPR-compliant Consent Banner with Google Consent Mode v2
 * Features: DNT support, consent expiry, modal settings, cookie deletion on revocation
 */
export function ConsentBanner({ locale = 'en' }: ConsentBannerProps) {
  // Start with false to avoid hydration mismatch, then check in useEffect
  const [isOpen, setIsOpen] = useState(false);
  
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentCategories>(() => {
    return getSavedConsent() || defaultConsent;
  });

  // Get current pathname to detect legal pages
  const pathname = usePathname();
  
  // Check if current page is a legal page (Privacy/Cookie/Terms)
  const isLegalPage = pathname ? (
    pathname.includes(ROUTES.privacy.en) ||
    pathname.includes(ROUTES.privacy.nl) ||
    pathname.includes(ROUTES.privacy.de) ||
    pathname.includes(ROUTES.cookies.en) ||
    pathname.includes(ROUTES.cookies.nl) ||
    pathname.includes(ROUTES.cookies.de) ||
    pathname.includes(ROUTES.terms.en) ||
    pathname.includes(ROUTES.terms.nl) ||
    pathname.includes(ROUTES.terms.de)
  ) : false;

  useEffect(() => {
    // Check if we need to show the banner (after hydration to avoid mismatch)
    if (shouldShowConsentBanner()) {
      // Check for DNT (Do Not Track)
      if (isDNTEnabled()) {
        // Auto-deny tracking for DNT users
        const dntConsent: ConsentCategories = {
          functional: true,
          analytics: false,
          marketing: false,
        };
        handleApplyConsent(dntConsent);
        setIsOpen(false);
      } else {
        // Show banner for regular users
        setIsOpen(true);
      }
    } else {
      // Apply saved consent
      const saved = getSavedConsent();
      if (saved) {
        applyConsentMode(saved);
        initializeAnalytics(saved);
      }
    }
  }, []);

  // Sync consent state with localStorage when banner opens
  useEffect(() => {
    if (isOpen) {
      const saved = getSavedConsent();
      if (saved) {
        setConsent(saved);
      }
    }
  }, [isOpen]);

  // Listen for custom event to open consent banner (from footer Cookie Settings button)
  useEffect(() => {
    const handleOpenBanner = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('openConsentBanner', handleOpenBanner);
    return () => window.removeEventListener('openConsentBanner', handleOpenBanner);
  }, []);

  const handleAcceptAll = () => {
    const fullConsent: ConsentCategories = {
      functional: true,
      analytics: true,
      marketing: true,
    };
    handleApplyConsent(fullConsent);
  };

  const handleRejectAll = () => {
    handleApplyConsent(defaultConsent);
  };

  const handleSavePreferences = () => {
    handleApplyConsent(consent);
  };

  const handleApplyConsent = (consentData: ConsentCategories) => {
    const previousConsent = getSavedConsent();
    
    // Check if user is revoking any previously granted consent
    if (previousConsent) {
      // Check if EITHER analytics OR marketing is being revoked
      const analyticsRevoked = previousConsent.analytics && !consentData.analytics;
      const marketingRevoked = previousConsent.marketing && !consentData.marketing;
      
      if (analyticsRevoked || marketingRevoked) {
        // Determine which type to pass to revokeConsent
        if (analyticsRevoked && marketingRevoked) {
          revokeConsent('all');
        } else if (analyticsRevoked) {
          revokeConsent('analytics');
        } else {
          revokeConsent('marketing');
        }
        return; // revokeConsent() will reload the page
      }
    }
    
    // Normal consent update (granting or maintaining consent)
    saveConsent(consentData);
    
    // Apply Google Consent Mode v2
    applyConsentMode(consentData);
    
    // Initialize analytics based on consent
    initializeAnalytics(consentData);
    
    // Close banner
    setIsOpen(false);
    setShowDetails(false);
  };

  const content = getContent(locale);
  const policyLinks = getPolicyLinks(locale || 'en');

  if (!isOpen) return null;

  return (
    <div 
      className={isLegalPage ? styles.backdropLegal : styles.backdrop} 
      role="dialog" 
      aria-modal={!isLegalPage ? "true" : "false"}
      aria-labelledby="consent-title"
    >
      <div className={styles.banner}>
        <div className={styles.content}>
          <h2 id="consent-title" className={styles.title}>
            {content.title}
          </h2>
          <Text size="sm" color="secondary">
            {content.description}
          </Text>
          
          {/* Policy links */}
          <div className={styles.policyLinks}>
            <Text size="xs" color="tertiary">
              {content.learnMore}{' '}
              <a href={policyLinks.privacy} className={styles.policyLink}>
                {content.privacyPolicy}
              </a>
              {' · '}
              <a href={policyLinks.cookies} className={styles.policyLink}>
                {content.cookiePolicy}
              </a>
            </Text>
          </div>

          {showDetails && (
            <div className={styles.details}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={consent.functional}
                  disabled
                  aria-label={content.functional}
                />
                <span>
                  <strong>{content.functional}</strong>
                  <Text size="xs" color="tertiary">{content.functionalDesc}</Text>
                </span>
              </label>

              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={consent.analytics}
                  onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                  aria-label={content.analytics}
                />
                <span>
                  <strong>{content.analytics}</strong>
                  <Text size="xs" color="tertiary">{content.analyticsDesc}</Text>
                </span>
              </label>

              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={consent.marketing}
                  onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                  aria-label={content.marketing}
                />
                <span>
                  <strong>{content.marketing}</strong>
                  <Text size="xs" color="tertiary">{content.marketingDesc}</Text>
                </span>
              </label>
            </div>
          )}

          <div className={styles.actions}>
            {!showDetails ? (
              <>
                <Button variant="primary" onClick={handleAcceptAll}>
                  {content.acceptAll}
                </Button>
                <Button variant="outline" onClick={() => setShowDetails(true)}>
                  {content.customize}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleRejectAll}>
                  {content.rejectAll}
                </Button>
              </>
            ) : (
              <>
                <Button variant="primary" onClick={handleSavePreferences}>
                  {content.save}
                </Button>
                <Button variant="ghost" onClick={() => setShowDetails(false)}>
                  {content.back}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getContent(locale: string) {
  const translations: Record<string, any> = {
    en: {
      title: 'Cookie Settings',
      description: 'We use cookies to improve your experience, analyze site traffic, and personalize content.',
      functional: 'Functional',
      functionalDesc: 'Required for the website to function properly.',
      analytics: 'Analytics',
      analyticsDesc: 'Help us understand how visitors interact with our website.',
      marketing: 'Marketing',
      marketingDesc: 'Used to track visitors across websites for advertising purposes.',
      acceptAll: 'Accept All',
      rejectAll: 'Reject All',
      customize: 'Customize',
      save: 'Save Preferences',
      back: 'Back',
      learnMore: 'Learn more:',
      privacyPolicy: 'Privacy Policy',
      cookiePolicy: 'Cookie Policy',
    },
    nl: {
      title: 'Cookie Instellingen',
      description: 'We gebruiken cookies om je ervaring te verbeteren, websiteverkeer te analyseren en inhoud te personaliseren.',
      functional: 'Functioneel',
      functionalDesc: 'Vereist voor het goed functioneren van de website.',
      analytics: 'Analytics',
      analyticsDesc: 'Help ons begrijpen hoe bezoekers onze website gebruiken.',
      marketing: 'Marketing',
      marketingDesc: 'Gebruikt om bezoekers te volgen voor advertentiedoeleinden.',
      acceptAll: 'Alles Accepteren',
      rejectAll: 'Alles Weigeren',
      customize: 'Aanpassen',
      save: 'Voorkeuren Opslaan',
      back: 'Terug',
      learnMore: 'Lees meer:',
      privacyPolicy: 'Privacybeleid',
      cookiePolicy: 'Cookiebeleid',
    },
    de: {
      title: 'Cookie-Einstellungen',
      description: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern, Website-Traffic zu analysieren und Inhalte zu personalisieren.',
      functional: 'Funktional',
      functionalDesc: 'Erforderlich für das ordnungsgemäße Funktionieren der Website.',
      analytics: 'Analytik',
      analyticsDesc: 'Helfen Sie uns zu verstehen, wie Besucher unsere Website nutzen.',
      marketing: 'Marketing',
      marketingDesc: 'Wird verwendet, um Besucher über Websites hinweg zu verfolgen.',
      acceptAll: 'Alle akzeptieren',
      rejectAll: 'Alle ablehnen',
      customize: 'Anpassen',
      save: 'Einstellungen speichern',
      back: 'Zurück',
      learnMore: 'Erfahren Sie mehr:',
      privacyPolicy: 'Datenschutzerklärung',
      cookiePolicy: 'Cookie-Richtlinie',
    },
  };

  return translations[locale] || translations.en;
}

function getPolicyLinks(locale: string) {
  const localeKey = (locale === 'nl' ? 'nl' : locale === 'de' ? 'de' : 'en') as 'en' | 'nl' | 'de';
  
  return {
    privacy: ROUTES.privacy[localeKey],
    cookies: ROUTES.cookies[localeKey],
  };
}
